import moment from "moment";
import {
  Autocomplete,
  Button,
  CheckboxField,
  Divider,
  Flex,
  Grid,
  Label,
  Loader,
  SelectField,
  TextAreaField,
  TextField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";
import { useContext, useEffect, useState } from "react";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";
import { HexColorPicker } from "react-colorful";
import SquareIcon from "@mui/icons-material/Square";

import useErrors from "hooks/useErrors";
import LabelRequired from "components/utils/LabelRequired";
import { globalStrings } from "config/translations/es";
import { LayoutContext } from "layout/context/LayoutContext";
import {
  costCentersByCompanyID,
  branchesByCompanyID,
  listCompanies,
  getEquipment,
} from "graphql/queries";
import CustomPopover from "components/utils/CustomPopover";
import ExecutorCard from "components/serviceOrders/ExecutorCard";
import SelectTool from "components/tools/SelectTool";
import SelectSpare from "components/spares/SelectSpare";
import SelectMaintenanceRoutine from "components/maintenanceRoutines/SelectMaintenanceRoutine";
import EquipmentsChoose from "components/equipments/EquipmentsChoose";
import EquipmentRoutines from "components/maintenanceRoutines/EquipmentRoutines";
import ServiceOrderSchedule from "./ServiceOrderSchedule";
import EquipmentSpares from "components/equipments/EquipmentSpares";
import { PlayArrow } from "@mui/icons-material";

const ServiceOrderForm = ({
  onSubmit,
  type = "create",
  initialValues,
  fromEquipment,
}) => {
  const { me } = useContext(LayoutContext);
  const [serviceOrderForm, setServiceOrderForm] = useState(initialValues);
  const [companies, setCompanies] = useState({
    list: [],
    loading: false,
  });
  const [branches, setBranches] = useState({
    list: [],
    loading: false,
  });
  const [costCenters, setCostCenters] = useState({
    list: [],
    loading: false,
  });
  const [loadingEquipmentRoutines, setLoadingEquipmentRoutines] =
    useState(false);
  const [resetValues, setResetValues] = useState(false);
  const [errors, setErrors] = useState({
    title: "",
    companyID: "",
    branchID: "",
    costCenterID: "",
  });
  const validateErrors = useErrors({
    title: "isRequired",
    companyID: "isRequired",
    branchID: "isRequired",
    costCenterID: "isRequired",
  });

  const onSubmitForm = () => {
    let hasErrors = false;

    Object.entries(serviceOrderForm).forEach((itemForm) => {
      const [name, value] = itemForm;
      const check = !!checkErrors(name, value);
      if (!hasErrors) hasErrors = check;
    });

    if (
      checkIncorrectValue("companyID", companies.list) ||
      checkIncorrectValue("branchID", branches.list) ||
      checkIncorrectValue("costCenterID", costCenters.list)
    )
      return;

    if (
      serviceOrderForm.end &&
      moment(serviceOrderForm.end).isSameOrBefore(
        serviceOrderForm.start,
        "minute"
      )
    ) {
      toast.error(strings.validations.endBeforeStart);
      return;
    }

    if (!!serviceOrderForm.serie && !serviceOrderForm.until) {
      toast.error(strings.validations.untilMissing);
      return;
    }

    serviceOrderForm.equipmentRoutines =
      serviceOrderForm.equipmentRoutines?.filter((eq) => !!eq.routineID);

    if (!hasErrors) onSubmit(serviceOrderForm);
  };

  const checkIncorrectValue = (name, list) => {
    if (!list.some((rec) => rec.id === serviceOrderForm[name])) {
      setErrors((prevSt) => ({
        ...prevSt,
        [name]: strings.validations.incorrectValue,
      }));
      return true;
    }
    return false;
  };

  const onChange = (e) => {
    let { name, value, checked } = e.target;
    if (name === "start" || name === "end")
      value = value ? moment(value).toDate() : null;
    if (name === "geolocation" || name === "photoRequired") value = checked;

    setServiceOrderForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const onChangeColor = (color) => {
    setServiceOrderForm((prevSt) => ({
      ...prevSt,
      color,
    }));
  };

  const selectUserExecutor = (userExecutor) => {
    if (serviceOrderForm.auxExecutors?.some((aux) => aux === userExecutor)) {
      toast.error(strings.validations.existedExecutor);
      return;
    }
    setServiceOrderForm((prevSt) => ({
      ...prevSt,
      userExecutor,
    }));
  };

  const onChangeTools = (tools) => {
    setServiceOrderForm((prevSt) => ({
      ...prevSt,
      tools,
    }));
  };

  const onChangeSpares = (spares) => {
    setServiceOrderForm((prevSt) => ({
      ...prevSt,
      spares,
    }));
  };

  const onChangeMaintenanceRoutines = (maintenanceRoutines) => {
    setServiceOrderForm((prevSt) => ({
      ...prevSt,
      maintenanceRoutines,
    }));
  };

  const onAddAuxExecutor = (id) => {
    if (
      serviceOrderForm.auxExecutors?.some((aux) => aux === id) ||
      serviceOrderForm.userExecutor === id
    ) {
      toast.error(strings.validations.existedExecutor);
      return;
    }
    setServiceOrderForm((prevSt) => ({
      ...prevSt,
      auxExecutors: [...(prevSt.auxExecutors ?? []), id],
    }));
  };

  const onRemoveAuxExecutor = (id) => {
    setServiceOrderForm((prevSt) => ({
      ...prevSt,
      auxExecutors: prevSt.auxExecutors?.filter((aux) => aux !== id) ?? [],
    }));
  };

  const onChangeEquipments = (equipments) => {
    setServiceOrderForm((prevSt) => ({
      ...prevSt,
      equipments,
    }));
    if (!equipments || equipments?.length <= 0) {
      setServiceOrderForm((prevSt) => ({
        ...prevSt,
        equipmentRoutines: [],
        equipmentSpares: [],
      }));
    } else {
      fetchEquipmentRoutines(equipments.map((eq) => eq.id));
      onChangeEquipmentSpares(
        equipments.map((equipment) => ({
          equipmentID: equipment.id,
          spares:
            serviceOrderForm.equipmentSpares?.find(
              (servSpare) => servSpare.equipmentID === equipment.id
            )?.spares ?? [],
        }))
      );
    }
  };

  const onChangeEquipmentRoutines = (equipmentRoutines) => {
    setServiceOrderForm((prevSt) => ({
      ...prevSt,
      equipmentRoutines,
    }));
  };

  const onChangeEquipmentSpares = (equipmentSpares) => {
    setServiceOrderForm((prevSt) => ({
      ...prevSt,
      equipmentSpares,
    }));
  };

  const onChangeSchedule = (name, value) => {
    if (name === "start" || name === "end")
      value = value ? moment(value).toDate() : null;

    setServiceOrderForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
  };

  const onChangeSelect = (e) => {
    const { name, value } = e.target;
    if (name === "companyID") {
      fetchCompanies({ name: value });
      onClearSelect("branchID");
      onClearSelect("costCenterID");
    }
    if (name === "branchID") {
      fetchBranches({ name: value }, serviceOrderForm.companyID);
    }
    if (name === "costCenterID")
      fetchCostCenters({ name: value }, me.companyID || serviceOrderForm.companyID);
    setServiceOrderForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const onSelect = (name, value) => {
    setServiceOrderForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
    if (name === "companyID") {
      onClearSelect("branchID", value);
      onClearSelect("costCenterID", value);
      onChangeSpares([]);
      onChangeTools([]);
    }
  };

  const onClearSelect = (name, parentId) => {
    const value = "";
    if (name === "companyID") {
      fetchCompanies({ name: value });
      onClearSelect("branchID");
      onClearSelect("costCenterID");
      onChangeSpares([]);
      onChangeTools([]);
    }
    if (name === "branchID") {
      if (!parentId) setBranches({ list: [], loading: false });
      else fetchBranches({ name: value }, parentId);
    }
    if (name === "costCenterID") {
      fetchCostCenters({ name: value }, me.companyID || serviceOrderForm.companyID);
    }
    setServiceOrderForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
  };

  const checkErrors = (name, value) => {
    const errorMessage = validateErrors(name, value);
    if (errorMessage)
      setErrors((prevErr) => ({ ...prevErr, [name]: errorMessage }));
    else setErrors((prevErr) => ({ ...prevErr, [name]: "" }));
    return errorMessage;
  };

  const resetFormValues = () => {
    setErrors({
      title: "",
      companyID: "",
      branchID: "",
      costCenterID: "",
    });
    setServiceOrderForm(initialValues);
    fetchCompanies({ id: initialValues.companyID });
    fetchBranches({ id: initialValues.branchID }, initialValues.companyID);
    fetchCostCenters({ id: initialValues.costCenterID }, me.companyID || initialValues.companyID );
    setResetValues((prev) => !prev);
  };

  async function fetchCompanies({ id = "", name = "" }) {
    let filterObj = {};
    try {
      setCompanies({ list: [], loading: true });

      if (me.companyID)
        filterObj = { ...filterObj, or: [{ id: { eq: me.companyID } }] };
      if (me.managedCompanies.length > 0)
        filterObj = {
          ...filterObj,
          or: [
            ...(filterObj?.or ?? []),
            ...me.managedCompanies.map((cp) => ({ id: { eq: cp } })),
          ],
        };

      const companiesData = await API.graphql(
        graphqlOperation(listCompanies, {
          limit:
            !id && !name && Object.keys(filterObj).length <= 0 ? 20 : undefined,
          filter: {
            and: [
              {
                id: { contains: id },
              },
              {
                name: { contains: name },
              },
            ],
            ...filterObj,
          },
        })
      );
      const companiesItems = companiesData.data.listCompanies.items;
      setCompanies((prev) => ({ ...prev, list: companiesItems }));
    } catch (err) {
      toast.error(strings.responses.errorFetchCompanies);
    } finally {
      setCompanies((prev) => ({ ...prev, loading: false }));
    }
  }

  async function fetchBranches({ id = "", name = "" }, companyId) {
    try {
      if (!companyId) return;
      setBranches({ list: [], loading: true });
      const branchesData = await API.graphql(
        graphqlOperation(branchesByCompanyID, {
          companyID: companyId,
          limit: !id && !name ? 20 : undefined,
          filter: {
            and: [
              {
                id: { contains: id },
              },
              {
                name: { contains: name },
              },
            ],
          },
        })
      );
      const branchesItems = branchesData.data.branchesByCompanyID.items;
      setBranches((prev) => ({ ...prev, list: branchesItems }));
    } catch (err) {
      toast.error(strings.responses.errorFetchBranches);
    } finally {
      setBranches((prev) => ({ ...prev, loading: false }));
    }
  }

  async function fetchCostCenters({ id = "", name = "" }, companyId) {
    try {
      if (!companyId) return;
      setCostCenters({ list: [], loading: true });
      const costCentersData = await API.graphql(
        graphqlOperation(costCentersByCompanyID, {
          companyID: companyId,
          limit: !id && !name ? 20 : undefined,
          filter: {
            and: [
              {
                id: { contains: id },
              },
              {
                name: { contains: name },
              },
            ],
            deletedAt: { attributeExists: false },
          },
        })
      );
      const costCentersItems =
        costCentersData.data.costCentersByCompanyID.items;
      setCostCenters((prev) => ({ ...prev, list: costCentersItems }));
    } catch (err) {
      toast.error(strings.responses.errorFetchCostCenters);
    } finally {
      setCostCenters((prev) => ({ ...prev, loading: false }));
    }
  }

  async function fetchEquipmentRoutines(ids) {
    try {
      setLoadingEquipmentRoutines(true);

      const equipmentItems = [];
      if (ids.length > 0) {
        await Promise.all(
          ids.map(async (id) => {
            const equipmentData = await API.graphql(
              graphqlOperation(getEquipment, { id })
            );
            if (
              equipmentData.data.getEquipment &&
              !equipmentData.data.getEquipment.deletedAt
            ) {
              equipmentItems.push(equipmentData.data.getEquipment);
            }
          })
        );
      }

      onChangeEquipmentRoutines(
        equipmentItems.map((equipment) => ({
          equipmentID: equipment.id,
          routineID:
            serviceOrderForm.equipmentRoutines?.find(
              (servRoutine) => servRoutine.equipmentID === equipment.id
            )?.routineID || equipment.routineID,
        }))
      );
    } catch (err) {
      console.log(err);
      toast.error(strings.responses.errorFetchEquipmentsRoutines);
    } finally {
      setLoadingEquipmentRoutines(false);
    }
  }

  useEffect(() => {
    setServiceOrderForm(initialValues);
    fetchCompanies({ id: initialValues.companyID });
    fetchBranches({ id: initialValues.branchID }, initialValues.companyID);
    fetchCostCenters({ id: initialValues.costCenterID }, me.companyID || initialValues.companyID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <>
      <Grid rowGap="15px" columnGap="15px" padding="20px">
        <Grid
          templateColumns={{ base: "1fr", large: "2fr 2fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <TextField
            name="code"
            label={strings.fields.code.label}
            value={serviceOrderForm.code}
            disabled
          ></TextField>
          <TextField
            name="title"
            label={<LabelRequired text={strings.fields.title.label} />}
            value={serviceOrderForm.title}
            onChange={onChange}
            errorMessage={errors.title}
            hasError={!!errors.title}
          ></TextField>
          {!fromEquipment && (
            <>
              <Flex direction="column" style={styles.autocomplete}>
                <Label htmlFor="companyID">
                  <LabelRequired text={strings.fields.company.label} />
                </Label>
                <Autocomplete
                  id="companyID"
                  name="companyID"
                  isRequired
                  disabled={type !== "create"}
                  placeholder={strings.fields.company.placeholder}
                  value={
                    companies.list.find(
                      (companyRec) =>
                        companyRec.id === serviceOrderForm.companyID
                    )?.name ?? serviceOrderForm.companyID
                  }
                  options={companies.list.map((companyRec) => ({
                    id: companyRec.id,
                    label: companyRec.name,
                  }))}
                  isLoading={companies.loading}
                  onClear={() =>
                    type === "create" && onClearSelect("companyID")
                  }
                  onChange={onChangeSelect}
                  onSelect={({ id }) => onSelect("companyID", id)}
                  errorMessage={errors.companyID}
                  hasError={!!errors.companyID}
                  menuSlots={{ Empty: globalStrings.noResults }}
                />
              </Flex>
              <Flex direction="column" style={styles.autocomplete}>
                <Label htmlFor="branchID">
                  <LabelRequired text={strings.fields.branch.label} />
                </Label>
                <Autocomplete
                  id="branchID"
                  name="branchID"
                  isRequired
                  disabled={!serviceOrderForm.companyID}
                  placeholder={strings.fields.branch.placeholder}
                  value={
                    branches.list.find(
                      (branchRec) => branchRec.id === serviceOrderForm.branchID
                    )?.name ?? serviceOrderForm.branchID
                  }
                  options={branches.list.map((branchRec) => ({
                    id: branchRec.id,
                    label: branchRec.name,
                  }))}
                  isLoading={branches.loading}
                  onClear={() =>
                    onClearSelect("branchID", serviceOrderForm.companyID)
                  }
                  onChange={onChangeSelect}
                  onSelect={({ id }) => onSelect("branchID", id)}
                  errorMessage={errors.branchID}
                  hasError={!!errors.branchID}
                  menuSlots={{ Empty: globalStrings.noResults }}
                />
              </Flex>
            </>
          )}
          <Flex direction="column" style={styles.autocomplete}>
            <Label htmlFor="costCenterID">
              <LabelRequired text={strings.fields.costCenter.label} />
            </Label>
            <Autocomplete
              id="costCenterID"
              name="costCenterID"
              isRequired
              placeholder={strings.fields.costCenter.placeholder}
              value={
                costCenters.list.find(
                  (costCenterRec) =>
                    costCenterRec.id === serviceOrderForm.costCenterID
                )?.name ?? serviceOrderForm.costCenterID
              }
              options={costCenters.list.map((costCenterRec) => ({
                id: costCenterRec.id,
                label: costCenterRec.name,
              }))}
              isLoading={costCenters.loading}
              onClear={() => onClearSelect("costCenterID")}
              onChange={onChangeSelect}
              onSelect={({ id }) => onSelect("costCenterID", id)}
              errorMessage={errors.costCenterID}
              hasError={!!errors.costCenterID}
              menuSlots={{ Empty: globalStrings.noResults }}
            />
          </Flex>
        </Grid>
        {!fromEquipment && (
          <>
            <Divider className="mt-3 mb-2" />
            <EquipmentsChoose
              initEquipmentsGranted={initialValues.equipments}
              onChangeEquipmentsGranted={onChangeEquipments}
              resetValues={resetValues}
              branch={serviceOrderForm.branchID}
            />
            {loadingEquipmentRoutines ? (
              <Loader />
            ) : (
              <EquipmentRoutines
                initialValues={serviceOrderForm.equipmentRoutines}
                companyID={me.companyID}
                onChange={onChangeEquipmentRoutines}
              />
            )}
            <EquipmentSpares
              initialValues={serviceOrderForm.equipmentSpares}
              companyID={serviceOrderForm.companyID}
              onChange={onChangeEquipmentSpares}
            />
          </>
        )}
        <Divider className="mt-3 mb-2" />
        <TextAreaField
          name="description"
          label={strings.fields.description.label}
          value={serviceOrderForm.description}
          onChange={onChange}
        ></TextAreaField>
        <Grid
          templateColumns={{ base: "1fr", large: "2fr 2fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <TextField
            name="billDate"
            label={strings.fields.billDate.label}
            value={serviceOrderForm.billDate}
            onChange={onChange}
            type="date"
          ></TextField>
          <TextField
            name="billValue"
            label={strings.fields.billValue.label}
            value={serviceOrderForm.billValue}
            onChange={onChange}
            type="number"
          ></TextField>
          <SelectField
            name="billCurrency"
            label={strings.fields.billCurrency.label}
            onChange={onChange}
            value={serviceOrderForm.billCurrency ?? ""}
          >
            <option value="">{globalStrings.selectOption}</option>
            {Object.entries(globalStrings.currency).map((opt) => (
              <option value={opt[0]}>{opt[1]}</option>
            ))}
          </SelectField>
          {!fromEquipment && (
            <>
              <Grid columnSpan={2} rowGap="15px" columnGap="15px">
                <Divider className="mt-3 mb-2" />
                <ServiceOrderSchedule
                  form={serviceOrderForm}
                  type={type}
                  onChange={onChangeSchedule}
                />
                <Divider className="mt-3 mb-2" />
              </Grid>
              <TextField
                name="color"
                label={strings.fields.color.label}
                value={serviceOrderForm.color}
                onChange={onChange}
                outerEndComponent={
                  <CustomPopover
                    content={
                      <HexColorPicker
                        style={{ margin: "20px" }}
                        color={serviceOrderForm.color || "#14547b"}
                        onChange={onChangeColor}
                      />
                    }
                  >
                    <Button color={serviceOrderForm.color || "#14547b"}>
                      <SquareIcon />
                    </Button>
                  </CustomPopover>
                }
              ></TextField>
              <TextField
                name="status"
                label={strings.fields.status.label}
                value={
                  serviceOrderForm.status &&
                  globalStrings.serviceOrderStatus[serviceOrderForm.status]
                }
                disabled
              ></TextField>
            </>
          )}
        </Grid>
        <TextAreaField
          name="observations"
          label={strings.fields.observations.label}
          value={serviceOrderForm.observations}
          onChange={onChange}
        ></TextAreaField>
        <Grid
          templateColumns={{ base: "1fr", large: "2fr 2fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <ExecutorCard
            executorID={serviceOrderForm.userExecutor}
            selectUserExecutor={selectUserExecutor}
            hasAuxExecutors
            auxExecutors={serviceOrderForm.auxExecutors}
            onAddAuxExecutor={onAddAuxExecutor}
            onRemoveAuxExecutor={onRemoveAuxExecutor}
          />
        </Grid>
        {!fromEquipment &&
          companies.list.find(
            (companyRec) => companyRec.id === serviceOrderForm.companyID
          )?.id && (
            <SelectTool
              initialValues={serviceOrderForm.tools}
              onChangeTools={onChangeTools}
              executor={serviceOrderForm.userExecutor}
              companyID={serviceOrderForm.companyID}
            />
          )}
        {companies.list.find(
          (companyRec) => companyRec.id === serviceOrderForm.companyID
        )?.id && (
          <SelectSpare
            initialValues={serviceOrderForm.spares}
            onChangeSpares={onChangeSpares}
            parentName="serviceOrderID"
            parentValue={serviceOrderForm.id}
            companyID={serviceOrderForm.companyID}
          />
        )}
        {companies.list.find(
          (companyRec) => companyRec.id === serviceOrderForm.companyID
        )?.id && (
          <SelectMaintenanceRoutine
            initialValues={serviceOrderForm.maintenanceRoutines}
            onChangeMaintenanceRoutines={onChangeMaintenanceRoutines}
            executor={serviceOrderForm.userExecutor}
            companyID={me.companyID}
          />
        )}
        {!fromEquipment && (
          <>
            <CheckboxField
              name="geolocation"
              label={strings.fields.geolocation.label}
              checked={serviceOrderForm.geolocation || false}
              onChange={onChange}
            ></CheckboxField>
            <CheckboxField
              name="photoRequired"
              label={strings.fields.photoRequired.label}
              checked={serviceOrderForm.photoRequired || false}
              onChange={onChange}
            ></CheckboxField>
          </>
        )}
        <Flex justifyContent="end">
          <Button
            children={strings.fields.buttons.reset}
            onClick={resetFormValues}
          ></Button>
          <Button
            onClick={onSubmitForm}
            variation="primary"
            isDisabled={Object.values(errors).some((e) => !!e)}
          >
            {}
            {!fromEquipment ? (
              type === "create" ? (
                strings.fields.buttons.create
              ) : (
                strings.fields.buttons.save
              )
            ) : (
              <Flex gap={2}>
                <PlayArrow /> {strings.startExecution}
              </Flex>
            )}
          </Button>
        </Flex>
      </Grid>
    </>
  );
};

const styles = {
  autocomplete: {
    rowGap: "8px",
  },
};

const strings = new LocalizedStrings({
  es: {
    startExecution: "Iniciar ejecución",
    fields: {
      code: {
        label: "Código",
      },
      title: {
        label: "Nombre",
      },
      description: {
        label: "Descripción",
      },
      billDate: {
        label: "Fecha de facturación",
      },
      billValue: {
        label: "Valor de la factura",
      },
      billCurrency: {
        label: "Moneda de la factura",
      },
      company: {
        label: "Empresa",
        placeholder: "Buscar empresa...",
      },
      branch: {
        label: "Sede",
        placeholder: "Buscar sede...",
      },
      equipment: {
        label: "Equipo",
        placeholder: "Buscar equipo...",
      },
      costCenter: {
        label: "Centro de Costo",
        placeholder: "Buscar centro...",
      },
      color: {
        label: "Color",
      },
      observations: {
        label: "Observaciones",
      },
      status: {
        label: "Estado",
      },
      geolocation: {
        label: "Activar geolocalización para la ejecución",
      },
      photoRequired: {
        label: "Exigir foto para finalizar la ejecución",
      },
      buttons: {
        create: "Crear",
        save: "Guardar",
        reset: "Restablecer",
      },
    },
    validations: {
      incorrectValue: "Valor incorrecto",
      endBeforeStart: "La fecha final debe ser mayor a la fecha inicio",
      existedExecutor: "El ejecutor ya está registrado en la orden",
      untilMissing: "La serie debe tener una fecha de finalización",
    },
    responses: {
      errorFetchCompanies: "Error al consultar empresas",
      errorFetchBranches: "Error al consultar sedes",
      errorFetchEquipments: "Error al consultar equipos",
      errorFetchCostCenters: "Error al consultar centros de costo",
      errorFetchEquipmentsRoutines: "Error al consultar rutinas de equipos",
    },
  },
});

export default ServiceOrderForm;
